import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { CheckCircle2, XCircle, Eye, Clock, RefreshCw, Search } from 'lucide-react';

function authAxios() {
  const token = localStorage.getItem('dwi_token');
  const inst = axios.create({ baseURL: '/api', headers: { Authorization: `Bearer ${token}` } });
  inst.interceptors.response.use(
    r => r,
    e => {
      if (e.response?.status === 401) {
        localStorage.removeItem('dwi_token');
        localStorage.removeItem('dwi_user');
        window.location.href = '/login';
        return;
      }
      return Promise.reject(e);
    }
  );
  return inst;
}

const STATUS_COLORS = {
  pending:  'bg-blue-500/10 text-blue-400 border-blue-500/25',
  approved: 'bg-green-500/10 text-green-400 border-green-500/25',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/25',
};

export default function AdminKYC() {
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [approveForm, setApproveForm] = useState({ tier: 'tier1', notes: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [action, setAction] = useState(null); // 'approve' | 'reject' | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = authAxios();
      const [pRes, sRes] = await Promise.all([
        api.get(`/kyc/admin/profiles?status=${filter}&limit=50`),
        api.get('/kyc/admin/stats'),
      ]);
      setProfiles(pRes.data?.profiles || []);
      setStats(sRes.data || null);
    } catch (err) {
      toast.error('Failed to load KYC profiles');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const viewProfile = async (profile) => {
    setSelected(profile);
    setAction(null);
    setApproveForm({ tier: 'tier1', notes: '' });
    setRejectReason('');
    try {
      const res = await authAxios().get(`/kyc/admin/profiles/${profile._id}`);
      setDocs(res.data?.documents || []);
    } catch (_) { setDocs([]); }
  };

  const handleApprove = async () => {
    if (!approveForm.tier) { toast.error('Select a tier'); return; }
    setActionLoading(true);
    try {
      await authAxios().post(`/kyc/admin/profiles/${selected._id}/approve`, approveForm);
      toast.success('KYC approved! Customer notified.');
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approval failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    setActionLoading(true);
    try {
      await authAxios().post(`/kyc/admin/profiles/${selected._id}/reject`, { reason: rejectReason });
      toast.success('KYC rejected. Customer notified.');
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Rejection failed');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = profiles.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.userId?.name || '').toLowerCase().includes(q) ||
           (p.userId?.email || '').toLowerCase().includes(q) ||
           (p.userId?.phone || '').toLowerCase().includes(q);
  });

  const statsMap = {};
  (stats?.byStatus || []).forEach(s => { statsMap[s._id] = s.count; });

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#0a0f1e', minHeight: '100vh', color: '#e2e8f0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 20px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 900, margin: 0 }}>KYC Review Queue</h1>
            <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>Review and approve customer identity documents</p>
          </div>
          <button onClick={load} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 16px', color: '#e2e8f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Pending',  value: statsMap.pending  || 0, color: '#3b82f6' },
            { label: 'Approved', value: statsMap.approved || 0, color: '#10b981' },
            { label: 'Rejected', value: statsMap.rejected || 0, color: '#ef4444' },
            { label: 'Total',    value: stats?.total      || 0, color: '#00A8DB' },
          ].map(s => (
            <div key={s.label} style={{ background: '#111827', border: '1px solid #1e293b', borderRadius: 12, padding: 16, textAlign: 'center' }}>
              <p style={{ fontSize: 28, fontWeight: 900, color: s.color, margin: 0 }}>{s.value}</p>
              <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {['pending', 'approved', 'rejected'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                background: filter === f ? '#00A8DB20' : '#1e293b', borderColor: filter === f ? '#00A8DB' : '#334155', color: filter === f ? '#00A8DB' : '#94a3b8' }}>
              {f}
            </button>
          ))}
          <div style={{ flex: 1, position: 'relative', minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone..."
              style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '7px 10px 7px 30px', color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
        </div>

        {/* Table */}
        <div style={{ background: '#111827', border: '1px solid #1e293b', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No {filter} KYC profiles found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1e293b' }}>
                  {['Customer', 'Document', 'Status', 'Submitted', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p._id} style={{ borderTop: '1px solid #1e293b' }} onMouseEnter={e => e.currentTarget.style.background = '#1a2234'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '12px 16px' }}>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{p.userId?.name || '—'}</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{p.userId?.email || '—'}</p>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#94a3b8', textTransform: 'capitalize' }}>
                      {p.tier1?.documentType?.replace(/_/g, ' ') || 'N/A'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'capitalize',
                        background: p.status === 'approved' ? '#10b98120' : p.status === 'rejected' ? '#ef444420' : '#3b82f620',
                        color: p.status === 'approved' ? '#10b981' : p.status === 'rejected' ? '#ef4444' : '#3b82f6',
                        border: `1px solid ${p.status === 'approved' ? '#10b98140' : p.status === 'rejected' ? '#ef444440' : '#3b82f640'}` }}>
                        {p.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>
                      {p.submittedAt ? new Date(p.submittedAt).toLocaleDateString() : p.tier1?.submittedAt ? new Date(p.tier1.submittedAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => viewProfile(p)}
                        style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '5px 12px', color: '#00A8DB', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Eye size={13} /> Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail Modal */}
        {selected && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
            <div style={{ background: '#111827', border: '1px solid #1e293b', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Review KYC — {selected.userId?.name}</h2>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20 }}>✕</button>
              </div>

              <div style={{ padding: 24 }}>
                {/* Customer info */}
                <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                      ['Name', selected.userId?.name],
                      ['Email', selected.userId?.email],
                      ['Phone', selected.userId?.phone],
                      ['Status', selected.status],
                      ['Submitted', selected.submittedAt ? new Date(selected.submittedAt).toLocaleString() : selected.tier1?.submittedAt ? new Date(selected.tier1.submittedAt).toLocaleString() : '—'],
                      ['Doc Type', (selected.tier1?.documentType || 'N/A').replace(/_/g, ' ')],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <p style={{ margin: 0, fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>{label}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 13, color: '#e2e8f0', textTransform: 'capitalize' }}>{String(val || '—')}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Documents */}
                {docs.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 10 }}>Uploaded Documents</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                      {docs.map((doc, i) => (
                        <a key={i} href={doc.fileUrl} target="_blank" rel="noreferrer"
                          style={{ display: 'block', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 10, textDecoration: 'none', textAlign: 'center' }}>
                          {doc.fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <img src={doc.fileUrl} alt={doc.documentType} style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 6, marginBottom: 6 }} />
                          ) : (
                            <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 24 }}>📄</div>
                          )}
                          <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{doc.documentType?.replace(/_/g, ' ')}</p>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* From user.kyc (legacy docs) */}
                {!docs.length && (selected.tier1?.documentFront || selected.tier1?.documentBack || selected.tier1?.selfie) && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 10 }}>Document Images</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                      {[['Front', selected.tier1?.documentFront], ['Back', selected.tier1?.documentBack], ['Selfie', selected.tier1?.selfie]].filter(([,url]) => url).map(([label, url]) => (
                        <a key={label} href={url} target="_blank" rel="noreferrer" style={{ display: 'block', borderRadius: 8, overflow: 'hidden', background: '#1e293b', border: '1px solid #334155', textDecoration: 'none' }}>
                          <img src={url} alt={label} style={{ width: '100%', height: 80, objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                          <p style={{ margin: 0, padding: '4px 8px', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>{label}</p>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejection reason (if rejected) */}
                {selected.status === 'rejected' && selected.rejectionReason && (
                  <div style={{ background: '#ef444420', border: '1px solid #ef444440', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                    <p style={{ margin: 0, fontSize: 12, color: '#ef4444' }}><strong>Previous Rejection Reason:</strong> {selected.rejectionReason}</p>
                  </div>
                )}

                {/* Action buttons (only for pending) */}
                {selected.status === 'pending' && (
                  <div>
                    {action === null && (
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button onClick={() => setAction('approve')}
                          style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #10b98140', background: '#10b98120', color: '#10b981', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <CheckCircle2 size={15} /> Approve
                        </button>
                        <button onClick={() => setAction('reject')}
                          style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #ef444440', background: '#ef444420', color: '#ef4444', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <XCircle size={15} /> Reject
                        </button>
                      </div>
                    )}

                    {action === 'approve' && (
                      <div style={{ background: '#10b98110', border: '1px solid #10b98125', borderRadius: 10, padding: 16 }}>
                        <p style={{ margin: '0 0 12px', color: '#10b981', fontWeight: 700, fontSize: 14 }}>Approve KYC</p>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Verification Tier</label>
                          <select value={approveForm.tier} onChange={e => setApproveForm(f => ({ ...f, tier: e.target.value }))}
                            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 12px', color: '#e2e8f0', fontSize: 13 }}>
                            <option value="tier1">Tier 1 — Basic (NIN + personal info)</option>
                            <option value="tier2">Tier 2 — Standard (+ address proof)</option>
                            <option value="tier3">Tier 3 — Full (+ face verification)</option>
                          </select>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes (optional)</label>
                          <textarea value={approveForm.notes} onChange={e => setApproveForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Internal notes..."
                            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 12px', color: '#e2e8f0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={handleApprove} disabled={actionLoading}
                            style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: '#10b981', border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: actionLoading ? 0.6 : 1 }}>
                            {actionLoading ? 'Approving...' : 'Confirm Approve'}
                          </button>
                          <button onClick={() => setAction(null)} style={{ padding: '9px 16px', borderRadius: 8, background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {action === 'reject' && (
                      <div style={{ background: '#ef444410', border: '1px solid #ef444425', borderRadius: 10, padding: 16 }}>
                        <p style={{ margin: '0 0 12px', color: '#ef4444', fontWeight: 700, fontSize: 14 }}>Reject KYC</p>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Rejection Reason *</label>
                          <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3}
                            placeholder="Explain clearly why the KYC is rejected so the customer can resubmit correctly..."
                            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 12px', color: '#e2e8f0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={handleReject} disabled={actionLoading}
                            style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: '#ef4444', border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: actionLoading ? 0.6 : 1 }}>
                            {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
                          </button>
                          <button onClick={() => setAction(null)} style={{ padding: '9px 16px', borderRadius: 8, background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selected.status !== 'pending' && (
                  <div style={{ background: '#1e293b', borderRadius: 8, padding: 12, marginTop: 8 }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                      This profile is already <strong style={{ textTransform: 'capitalize' }}>{selected.status}</strong>.
                      {selected.status === 'rejected' ? ' Customer can re-submit documents.' : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
